import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import CitationBadge from 'components/shared/ai/CitationBadge';
import Button from 'components/shared/Button';
import { SummaryAttachment } from 'models/aiArtifacts/Summary';
import colors from 'styles/theme/colors';
import ChevronDownBold from 'svg/ChevronDownBold';
import ChevronRightBold from 'svg/ChevronRightBold';

type HeaderProps = {
  isExpanded: boolean;
  setIsExpanded: (isExpanded: boolean) => void;
  title: string;
  attachments: SummaryAttachment[];
};

function Header({ isExpanded, setIsExpanded, title, attachments }: HeaderProps) {
  const sortedAttachments = useMemo(() => {
    return [...attachments].sort((a, b) => a.displayId - b.displayId);
  }, [attachments]);

  return (
    <HeaderContainer isExpanded={isExpanded}>
      <Button variant='icon' onClick={() => setIsExpanded(!isExpanded)}>
        {isExpanded ? (
          <ChevronDownBold width={10} height={10} fill={colors.black} />
        ) : (
          <ChevronRightBold width={10} height={10} fill={colors.black} />
        )}
      </Button>
      <HeaderText>{title}</HeaderText>
      <CitationsContainer>
        {sortedAttachments.map((attachment) => (
          <CitationBadge key={attachment.attachmentId} size={14} attachment={attachment} />
        ))}
      </CitationsContainer>
    </HeaderContainer>
  );
}

type SectionProps = {
  title: string;
  attachments: { attachmentId: string; displayId: number; filename: string }[];
} & React.PropsWithChildren;

export default function Section({ title, attachments, children }: SectionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  return (
    <Container>
      <Header isExpanded={isExpanded} setIsExpanded={setIsExpanded} title={title} attachments={attachments} />
      {isExpanded && <ContentContainer>{children}</ContentContainer>}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  background-color: ${colors.black03};
  border-radius: 8px;
`;

const HeaderContainer = styled.div<{ isExpanded: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 24px;
  ${({ isExpanded }) =>
    isExpanded &&
    css`
      padding-bottom: 16px;
    `}
`;

const HeaderText = styled.h2`
  font-weight: 700;
  font-size: 14px;
  line-height: 22px;
  letter-spacing: 0.2px;
  margin-left: 16px;
  margin-right: 8px;

  color: ${colors.black};
`;

const CitationsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
`;

const ContentContainer = styled.div`
  padding: 0 50px 24px;
`;
